import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFieldsCompact from '../../components/form/fieldsCompact';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';

import localUtil from '../../lib/util';
import { conventionYearOf, isConventionMember, isClubRangeOfficer } from '../../lib/conventionResults';
import { snapshotSkillClass } from '../../lib/conventionKanban';
import { syncShooterFromRubic } from '../../lib/conventionRubicSync';
import { branchIdForWeaponClass } from '../../lib/rubicClub';
import NotMemberBadge from './notMemberBadge';
import IpscSignupFields from './ipscSignupFields';
import { isIpscStage } from '../../lib/ipscParticipant';
import { appConfirm } from '../../lib/dialogs';

function participantExists(participants, key, value) {
    return participants.some(participant => participant[key] === value);
}

@observer
class ConventionSignup extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            refreshing: false,
        };
    }

    updateField = async (e) => {
        const { id, md5, stage } = this.props;
        const { value, name, type, checked } = e.target;
        const { field } = e.target.dataset;
        const { conventionStore } = this.props.stores;
        const finalValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
        await conventionStore.editArrayObject({
            field,
            key: name,
            value: finalValue,
            id,
            md5,
        });
    }

    // Changing division on an entry that already has sheets leaves those sheets
    // under the old division (the results key on division) — warn before doing it.
    updateIpscField = async (name, value, participantFieldName, currentParticipant = {}) => {
        const { conventionIpscScoreStore } = this.props.stores;
        if (name === 'class' && currentParticipant.class && value !== currentParticipant.class) {
            const hasSheets = (conventionIpscScoreStore.conventionIpscScores || [])
                .some(r => Number(r.participant) === Number(currentParticipant.id) && r.division === currentParticipant.class);
            if (hasSheets) {
                const ok = await appConfirm('Skytteren har allerede poengskjemaer i denne divisjonen. De blir liggende under den gamle divisjonen til en funksjonær sletter dem. Bytte divisjon likevel?');
                if (!ok) return;
            }
        }
        await this.updateField({
            target: {
                name, value, type: 'select', dataset: { field: participantFieldName },
            },
        });
    }

    componentDidMount() {
        // IPSC: the sheets are needed for the division-change warning above.
        const { id, stage } = this.props;
        const { conventionStore, conventionIpscScoreStore } = this.props.stores;
        const { conventions = [], convention } = conventionStore;
        const current = conventions.find(c => c.id === parseInt(id, 10)) || convention || {};
        const currentStage = (current.stages || []).find(s => s.md5 === stage);
        if (isIpscStage(currentStage) && conventionIpscScoreStore.loadedConvention !== current.id) {
            conventionIpscScoreStore.loadForConvention(current.id);
        }
    }

    refreshFromNsf = async () => {
        const { id, md5, stage, idx } = this.props;
        const { conventionStore, userStore, appState } = this.props.stores;
        const { conventions = [], convention } = conventionStore;
        const currentConvention = conventions.find(c => c.id === parseInt(id, 10)) || convention;
        const idxInt = parseInt(idx, 10);
        const participantField = `participants${idxInt + 1}`;
        const participant = (currentConvention[participantField] || []).find(p => p.md5 === md5);
        if (!participant) return;
        const currentStage = (currentConvention.stages || []).find(s => s.md5 === stage) || {};
        const conventionYear = conventionYearOf(currentConvention);

        this.setState({ refreshing: true });
        try {
            // Branch (gren) of the participant's weapon class: the club is the one the shooter
            // represents in that branch, not the first active Rubic membership.
            await conventionStore.loadDisciplineMap();
            const branchId = branchIdForWeaponClass(conventionStore.disciplineMap, participant.class);
            const res = await syncShooterFromRubic({
                participant,
                conventionYear,
                appState,
                userStore,
                alwaysResolveClub: true,
                branchId,
            });
            if (!res.ok) {
                appState.addToast({
                    type: 'error',
                    message: res.notFound ? 'Fant ikke skytteren i NSF/Rubic' : 'Kunne ikke oppdatere fra NSF',
                }, 5000);
                this.setState({ refreshing: false });
                return;
            }

            // Gender (authoritative; leave unchanged if NSF gave nothing usable).
            if (res.genderCode) {
                await conventionStore.editArrayObject({ field: participantField, key: 'gender', value: res.genderCode, id, md5 });
            } else if (res.rawGender) {
                appState.addToast({ type: 'warning', message: `NSF-kjønn ikke gjenkjent: ${res.rawGender}` }, 5000);
            }

            // Re-snapshot club onto the participant.
            if (res.club && res.club.sportsClub) {
                await conventionStore.editArrayObject({ field: participantField, key: 'sportsClub', value: res.club.sportsClub, id, md5 });
                await conventionStore.editArrayObject({ field: participantField, key: 'nsfClubNumber', value: res.club.nsfClubNumber || '', id, md5 });
            }

            // Re-derive skytterklasse from the fresh classifications for the current weapon class.
            await conventionStore.loadClassMap();
            await conventionStore.loadDisciplineMap();
            const { classMap = [], disciplineMap = [] } = conventionStore;
            const derived = snapshotSkillClass({
                weaponClass: participant.class,
                manualSkillClass: null,
                user: { nsfDiciplineClasses: res.currentClasses },
                stage: currentStage,
                classMap,
                disciplineMap,
            });
            if (derived) {
                await conventionStore.editArrayObject({ field: participantField, key: 'skillClass', value: derived, id, md5 });
            }

            appState.addToast({ type: 'success', message: `Oppdatert fra NSF: ${res.name}` }, 4000);
            if (this.props.callback) this.props.callback();
        } catch (err) {
            appState.addToast({ type: 'error', message: err.message || 'Kunne ikke oppdatere fra NSF' }, 5000);
        }
        this.setState({ refreshing: false });
    }

    closeDrawer = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.closeDrawer(drawerLevel);
    }

    render() {
        const {
            signedUp,
            refreshing,
        } = this.state;
        const { id, md5, stage, idx } = this.props;
        const idxInt = parseInt(idx, 10);
        const { appState, userStore, conventionStore } = this.props.stores;
        const { user = {}, isAdmin, isCustomerAdmin, isRangeOfficer, isTester, isVeterinary, language } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { conventions = [], convention } = conventionStore;
        // console.log('conventions', conventions);
        // console.log('convention', convention);
        const currentConvention = conventions.find(c => c.id === parseInt(id, 10)) || convention;

        // "Oppdater fra NSF" is an organizer/admin action — hide it from regular participants.
        const isConventionAdmin = isAdmin
            || isCustomerAdmin
            || isConventionMember(currentConvention, user)
            || isClubRangeOfficer(currentConvention, user, isRangeOfficer)
            || (!!user.id && currentConvention.user === user.id);

        const currentStage = currentConvention.stages?.find(s => s.md5 === stage);
        const currentStageIndex = currentConvention.stages?.findIndex(s => s.md5 === stage);
        // const participantFieldName = `participants${currentStageIndex + 1}`;
        const participantFieldName = `participants${idxInt + 1}`;
        const currentParticipant = currentConvention[participantFieldName] ? currentConvention[participantFieldName].find(p => p.md5 === md5) || {} : {};
        // IPSC squads: division / power factor / category replace the NSF class fields.
        const isIpsc = isIpscStage(currentStage);
        const updateIpscField = (name, value) => this.updateIpscField(name, value, participantFieldName, currentParticipant);

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    {/* id: {id}<br /> */}
                    {/* md5: {md5}<br /> */}
                    {/* stage: {stage}<br /> */}
                    {/* participantFieldName: {participantFieldName}<br /> */}
                    {/* <xmp>{JSON.stringify(currentStage, null, 2)}</xmp> */}
                    {/* <xmp>{JSON.stringify(currentParticipant, null, 2)}</xmp> */}

                    <div class='w-100 d-flex flex-column'>
                        <div class='px-0 box-header-info d-flex flex-column'>
                            <div class={`d-flex flex-column mt-3 mx-3 px-3 py-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                                {/* The participant is always the current user for regular
                                    participants ("it is always them"), so only show the
                                    name header + NSF refresh to organizers/admins. */}
                                {isConventionAdmin && (
                                    <div class='d-flex flex-column'>
                                        <span class='text-muted font-weight-light'>
                                            <Text id='convention.participant'>Participant</Text>
                                        </span>
                                        <span>
                                            {currentParticipant.firstname} {currentParticipant.lastname}
                                            <NotMemberBadge participant={currentParticipant} convention={currentConvention} />
                                        </span>
                                        <div class='mt-2'>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-outline-primary rounded-pill'
                                                onClick={this.refreshFromNsf}
                                                disabled={refreshing}
                                            >
                                                {refreshing ? (
                                                    <span>
                                                        <i class='fa-duotone fa-spinner-third fa-spin me-1' />
                                                        <Text id='convention.refresh-nsf-loading'>Oppdaterer…</Text>
                                                    </span>
                                                ) : (
                                                    <span>
                                                        <i class='fa-duotone fa-cloud-arrow-down me-1' />
                                                        <Text id='convention.refresh-nsf'>Oppdater fra NSF</Text>
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {isIpsc && (
                                    <IpscSignupFields
                                        division={currentParticipant.class || ''}
                                        powerFactor={currentParticipant.powerFactor || 'minor'}
                                        ipscCategory={currentParticipant.ipscCategory || ''}
                                        onChange={updateIpscField}
                                    />
                                )}
                                {!isIpsc && <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.choose-class'>Choose shooting class</Text>
                                    </span>
                                    <select
                                        class='form-control rounded-pill'
                                        onInput={this.updateField}
                                        name={'class'}
                                        value={currentParticipant.class}
                                        data-field={participantFieldName}
                                    >
                                        <option value=''>-- <Text id='convention.choose-class'>Choose shooting class</Text> --</option>
                                        <option value='fin'>Fin</option>
                                        <option value='grov'>Grov</option>
                                        <option value='mil'>Militær</option>
                                        <option value='milrp'>Militær RP</option>
                                        <option value='rev'>Revolver</option>
                                        <option value='revrp'>Revolver RP</option>
                                        <option value='sp.rev'>Sp.rev</option>
                                        <option value='sp.pistol'>Sp.pistol</option>
                                        <option value='mag1'>Magnum 1</option>
                                        <option value='mag2'>Magnum 2</option>
                                        <option value='luft'>Luft</option>
                                    </select>
                                </div>}

                                {/* Class/age/gender/championship/result-status are organizer
                                    fields — regular participants only pick their discipline. */}
                                {isConventionAdmin && (<>
                                {!isIpsc && (<>
                                <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.skill-class'>Skill class</Text>
                                    </span>
                                    <select
                                        class='form-control rounded-pill'
                                        onInput={this.updateField}
                                        name='skillClass'
                                        value={currentParticipant.skillClass || ''}
                                        data-field={participantFieldName}
                                    >
                                        <option value=''>-- <Text id='convention.skill-class-auto'>Automatic (from Rubic)</Text> --</option>
                                        <option value='A'>A</option>
                                        <option value='B'>B</option>
                                        <option value='C'>C</option>
                                        <option value='D'>D</option>
                                    </select>
                                </div>

                                <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.age-class'>Age class</Text>
                                    </span>
                                    <select
                                        class='form-control rounded-pill'
                                        onInput={this.updateField}
                                        name='ageClass'
                                        value={currentParticipant.ageClass || ''}
                                        data-field={participantFieldName}
                                    >
                                        <option value=''>-- <Text id='convention.no-age-class'>None</Text> --</option>
                                        <option value='u'>U (under 16)</option>
                                        <option value='jr'>Jr</option>
                                        <option value='v55'>V55</option>
                                        <option value='v65'>V65</option>
                                        <option value='v73'>V73</option>
                                    </select>
                                </div>

                                <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.gender'>Gender</Text>
                                    </span>
                                    <select
                                        class='form-control rounded-pill'
                                        onInput={this.updateField}
                                        name='gender'
                                        value={currentParticipant.gender || ''}
                                        data-field={participantFieldName}
                                    >
                                        <option value=''>-- <Text id='convention.no-gender'>None</Text> --</option>
                                        <option value='k'><Text id='convention.gender-k'>Female (K)</Text></option>
                                        <option value='m'><Text id='convention.gender-m'>Male (M)</Text></option>
                                    </select>
                                </div>

                                </>)}
                                <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.championship'>Championship</Text>
                                    </span>
                                    <div class='form-check form-switch'>
                                        <input
                                            type='checkbox'
                                            class='form-check-input'
                                            id={`participant-isKM-${md5}`}
                                            name='isKM'
                                            data-field={participantFieldName}
                                            checked={!!parseInt(currentParticipant.isKM, 10)}
                                            onInput={this.updateField}
                                        />
                                        <label class='form-check-label' for={`participant-isKM-${md5}`}>
                                            <Text id='convention.isKM'>Tellende for KM (Kretsmesterskap)</Text>
                                        </label>
                                    </div>
                                    <div class='form-check form-switch'>
                                        <input
                                            type='checkbox'
                                            class='form-check-input'
                                            id={`participant-isDM-${md5}`}
                                            name='isDM'
                                            data-field={participantFieldName}
                                            checked={!!parseInt(currentParticipant.isDM, 10)}
                                            onInput={this.updateField}
                                        />
                                        <label class='form-check-label' for={`participant-isDM-${md5}`}>
                                            <Text id='convention.isDM'>Tellende for DM (Distriktsmesterskap)</Text>
                                        </label>
                                    </div>
                                </div>

                                <div class='d-flex flex-column mt-3'>
                                    <span class='text-muted font-weight-light'>
                                        <Text id='convention.participant-status'>Result status</Text>
                                    </span>
                                    <select
                                        class='form-control rounded-pill'
                                        onInput={this.updateField}
                                        name='resultStatus'
                                        value={(currentParticipant.resultStatus || '').toString().toLowerCase()}
                                        data-field={participantFieldName}
                                    >
                                        <option value=''>-- <Text id='convention.status-none'>Finished / normal</Text> --</option>
                                        <option value='dnf'><Text id='convention.status-dnf'>DNF – did not finish</Text></option>
                                        <option value='dns'><Text id='convention.status-dns'>DNS – did not start</Text></option>
                                        <option value='dsq'><Text id='convention.status-dsq'>DSQ – disqualified</Text></option>
                                    </select>
                                </div>
                                </>)}

                                <div class='d-flex flex-column mt-3'>
                                    <button class='btn btn-primary rounded-pill' onClick={this.closeDrawer}>
                                        <Text id='convention.save-class'>Save shooting class</Text>
                                    </button>

                                </div>
                            </div>

                        </div>
                    </div>

                    {/* <SimpleBlog stores={this.props.stores} {...this.props} /> */}
                </div>
            </div>
        </>);
    }
}

export default ConventionSignup;
